import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  TrendingDown,
  Inventory,
  MonetizationOn,
  Speed,
  Warning,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { inventoryReportsService, StockLevelDto } from '../../services/inventoryReports.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { Branch } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = {
  en: {
    title: 'Slow Moving Items Report',
    titleAr: 'تقرير البضاعة الراكدة',
    all: 'All Items',
    slow30: 'Slow (30+ Days)',
    slow60: 'Very Slow (60+ Days)',
    slow90: 'Critical (90+ Days)',
    slow180: 'Dead Stock (180+ Days)',
    byWarehouse: 'By Warehouse',
    byBranch: 'By Branch',
    warehouse: 'Warehouse',
    branch: 'Branch',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    period: 'Inactivity Period',
    days30: '30+ Days',
    days60: '60+ Days',
    days90: '90+ Days',
    days180: '180+ Days',
    itemCode: 'Item Code',
    itemName: 'Item',
    quantity: 'Qty',
    estimatedCost: 'Est. Cost',
    totalValue: 'Total Value',
    lastUpdated: 'Last Update',
    daysInactive: 'Days Inactive',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    status: 'Status',
    recommendation: 'Action',
    totalItems: 'Total Items',
    slowItems: 'Slow Items',
    frozenValue: 'Frozen Value',
    avgDaysInactive: 'Avg Days Inactive',
    promote: 'Promote Sale',
    discount: 'Deep Discount',
    stopBuying: 'Stop Purchasing',
    dispose: 'Consider Disposal',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    noData: 'No slow moving items',
    loading: 'Loading...',
    kwd: 'KWD',
    itemsCount: 'Items',
    criticalAlert: 'items have no movement for 90+ days!',
  },
  ar: {
    title: 'تقرير البضاعة الراكدة',
    titleAr: 'تقرير البضاعة الراكدة',
    all: 'جميع الأصناف',
    slow30: 'راكدة (30+ يوم)',
    slow60: 'راكدة جداً (60+ يوم)',
    slow90: 'حرجة (90+ يوم)',
    slow180: 'ميتة (180+ يوم)',
    byWarehouse: 'حسب المخزن',
    byBranch: 'حسب الفرع',
    warehouse: 'المخزن',
    branch: 'الفرع',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    period: 'فترة الركود',
    days30: '30+ يوم',
    days60: '60+ يوم',
    days90: '90+ يوم',
    days180: '180+ يوم',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    quantity: 'الكمية',
    estimatedCost: 'التكلفة التقديرية',
    totalValue: 'القيمة الإجمالية',
    lastUpdated: 'آخر تحديث',
    daysInactive: 'أيام الركود',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    status: 'الحالة',
    recommendation: 'الإجراء',
    totalItems: 'إجمالي الأصناف',
    slowItems: 'الأصناف الراكدة',
    frozenValue: 'القيمة المُجمدة',
    avgDaysInactive: 'متوسط أيام الركود',
    promote: 'عرض ترويجي',
    discount: 'تخفيض كبير',
    stopBuying: 'إيقاف الشراء',
    dispose: 'التفكير بالتخلص',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    noData: 'لا توجد بضاعة راكدة',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    itemsCount: 'صنف',
    criticalAlert: 'بدون حركة لأكثر من 90 يوم!',
  },
};

interface SlowMovingItem {
  stockLevelId: number;
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  warehouseId?: number;
  warehouseName: string;
  warehouseNameAr: string;
  branchId?: number;
  branchName: string;
  branchNameAr: string;
  quantity: number;
  estimatedCost: number;
  totalValue: number;
  lastUpdated?: string;
  daysInactive: number;
  status: 'slow30' | 'slow60' | 'slow90' | 'slow180';
}

interface LocationSummary {
  locationId?: number;
  locationName: string;
  locationNameAr: string;
  totalItems: number;
  slowItems: number;
  frozenValue: number;
  avgDaysInactive: number;
}

const SlowMovingReport: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const t = translations[language];
  const isRTL = language === 'ar';

  const [stockLevels, setStockLevels] = useState<StockLevelDto[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [periodFilter, setPeriodFilter] = useState<number>(30);

  useEffect(() => {
    loadWarehouses();
    loadBranches();
    loadCompanyInfo();
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [warehouseFilter, branchFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await inventoryReportsService.getStockLevels({
        warehouseId: warehouseFilter || undefined,
        branchId: branchFilter || undefined,
        pageSize: 99999,
      });
      setStockLevels(result.data);
    } catch (error) {
      console.error('Failed to load stock levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysInactive = (lastUpdated?: string): number => {
    if (!lastUpdated) return 999; // افتراض أنها قديمة جداً
    const today = new Date();
    const lastDate = new Date(lastUpdated);
    const diffTime = today.getTime() - lastDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatus = (daysInactive: number): 'slow30' | 'slow60' | 'slow90' | 'slow180' => {
    if (daysInactive >= 180) return 'slow180';
    if (daysInactive >= 90) return 'slow90';
    if (daysInactive >= 60) return 'slow60';
    return 'slow30';
  };

  const slowMovingItems = useMemo((): SlowMovingItem[] => {
    const items: SlowMovingItem[] = [];

    stockLevels.forEach(stock => {
      const daysInactive = calculateDaysInactive(stock.lastUpdated);

      if (daysInactive >= periodFilter && (stock.quantity || 0) > 0) {
        items.push({
          stockLevelId: stock.itemId,
          itemId: stock.itemId,
          itemCode: stock.itemCode,
          itemName: stock.itemName,
          itemNameAr: stock.itemNameAr,
          warehouseId: stock.warehouseId,
          warehouseName: isRTL ? (stock.warehouseNameAr || stock.warehouseName || '-') : (stock.warehouseName || '-'),
          warehouseNameAr: stock.warehouseNameAr || '-',
          branchId: stock.branchId,
          branchName: isRTL ? (stock.branchNameAr || stock.branchName || '-') : (stock.branchName || '-'),
          branchNameAr: stock.branchNameAr || '-',
          quantity: stock.quantity || 0,
          estimatedCost: stock.unitCost || 0,
          totalValue: stock.totalValue || 0,
          lastUpdated: stock.lastUpdated,
          daysInactive,
          status: getStatus(daysInactive),
        });
      }
    });

    return items.sort((a, b) => b.daysInactive - a.daysInactive);
  }, [stockLevels, periodFilter, isRTL]);

  const getFilteredItems = (status?: string): SlowMovingItem[] => {
    if (!status) return slowMovingItems;
    return slowMovingItems.filter(i => i.status === status);
  };

  const warehouseSummary = useMemo((): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();

    slowMovingItems.forEach(item => {
      if (item.warehouseId) {
        if (!summary.has(item.warehouseId)) {
          summary.set(item.warehouseId, {
            locationId: item.warehouseId,
            locationName: item.warehouseName,
            locationNameAr: item.warehouseNameAr,
            totalItems: 0,
            slowItems: 0,
            frozenValue: 0,
            avgDaysInactive: 0,
          });
        }

        const loc = summary.get(item.warehouseId)!;
        loc.totalItems++;
        loc.slowItems++;
        loc.frozenValue += item.totalValue;
        loc.avgDaysInactive += item.daysInactive;
      }
    });

    summary.forEach(loc => {
      loc.avgDaysInactive = loc.totalItems > 0 ? loc.avgDaysInactive / loc.totalItems : 0;
    });

    return Array.from(summary.values()).sort((a, b) => b.frozenValue - a.frozenValue);
  }, [slowMovingItems]);

  const branchSummary = useMemo((): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();

    slowMovingItems.forEach(item => {
      if (item.branchId) {
        if (!summary.has(item.branchId)) {
          summary.set(item.branchId, {
            locationId: item.branchId,
            locationName: item.branchName,
            locationNameAr: item.branchNameAr,
            totalItems: 0,
            slowItems: 0,
            frozenValue: 0,
            avgDaysInactive: 0,
          });
        }

        const loc = summary.get(item.branchId)!;
        loc.totalItems++;
        loc.slowItems++;
        loc.frozenValue += item.totalValue;
        loc.avgDaysInactive += item.daysInactive;
      }
    });

    summary.forEach(loc => {
      loc.avgDaysInactive = loc.totalItems > 0 ? loc.avgDaysInactive / loc.totalItems : 0;
    });

    return Array.from(summary.values()).sort((a, b) => b.frozenValue - a.frozenValue);
  }, [slowMovingItems]);

  const stats = useMemo(() => {
    return {
      totalItems: slowMovingItems.length,
      slow30Count: slowMovingItems.filter(i => i.status === 'slow30').length,
      slow60Count: slowMovingItems.filter(i => i.status === 'slow60').length,
      slow90Count: slowMovingItems.filter(i => i.status === 'slow90').length,
      slow180Count: slowMovingItems.filter(i => i.status === 'slow180').length,
      frozenValue: slowMovingItems.reduce((sum, i) => sum + i.totalValue, 0),
      avgDaysInactive: slowMovingItems.length > 0 ? slowMovingItems.reduce((sum, i) => sum + i.daysInactive, 0) / slowMovingItems.length : 0,
    };
  }, [slowMovingItems]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'slow180': return 'error';
      case 'slow90': return 'error';
      case 'slow60': return 'warning';
      case 'slow30': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'slow180': return t.slow180;
      case 'slow90': return t.slow90;
      case 'slow60': return t.slow60;
      case 'slow30': return t.slow30;
      default: return '';
    }
  };

  const getRecommendation = (daysInactive: number) => {
    if (daysInactive >= 180) return t.dispose;
    if (daysInactive >= 90) return t.stopBuying;
    if (daysInactive >= 60) return t.discount;
    return t.promote;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:K1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF8B4513' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('A5:K5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF8B4513' }
      };

      const data = slowMovingItems;
      const headers = [
        t.itemCode, t.itemName, t.warehouseName,
        t.quantity, t.estimatedCost, t.totalValue,
        t.lastUpdated, t.daysInactive, t.status, t.recommendation
      ];
      
      const headerRow = worksheet.getRow(8);
      headers.forEach((header, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF8B4513' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      
      let currentRow = 9;
      data.forEach((item) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = item.itemCode;
        row.getCell(2).value = isRTL ? item.itemNameAr : item.itemName;
        row.getCell(3).value = item.warehouseName;
        row.getCell(4).value = item.quantity;
        row.getCell(5).value = item.estimatedCost;
        row.getCell(6).value = item.totalValue;
        row.getCell(7).value = formatDate(item.lastUpdated);
        row.getCell(8).value = item.daysInactive;
        row.getCell(9).value = getStatusLabel(item.status);
        row.getCell(10).value = getRecommendation(item.daysInactive);
        
        const color = item.status === 'slow180' || item.status === 'slow90' ? 'FFFFE0E0' : 
                     item.status === 'slow60' ? 'FFFFF4E0' : 'FFE0F7FF';
        row.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: color }
          };
        });
        
        currentRow++;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SlowMoving_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 240, 230);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(139, 69, 19);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFillColor(139, 69, 19);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      const tableHeaders = [
        t.itemName,
        t.quantity,
        t.totalValue,
        t.daysInactive,
        t.status,
        t.recommendation,
      ];

      const tableData: any[] = [];
      const pdfItems = slowMovingItems;

      pdfItems.forEach((item) => {
        tableData.push([
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 25),
          formatCurrency(item.quantity),
          formatCurrency(item.totalValue),
          item.daysInactive.toString(),
          getStatusLabel(item.status),
          getRecommendation(item.daysInactive),
        ]);
      });

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'grid',
        headStyles: {
          fillColor: [139, 69, 19],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.row.index < pdfItems.length) {
            const item = pdfItems[data.row.index];
            if (item.status === 'slow180' || item.status === 'slow90') {
              data.cell.styles.fillColor = [255, 224, 224];
            } else if (item.status === 'slow60') {
              data.cell.styles.fillColor = [255, 244, 224];
            }
          }
        },
      });

      doc.save(`SlowMoving_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #8B4513 30%, #D2691E 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} sx={{ color: '#8B4513' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      {(stats.slow90Count + stats.slow180Count) > 0 && (
        <Alert severity="error" icon={<Warning />} sx={{ mb: 3 }} className="no-print">
          <strong>{stats.slow90Count + stats.slow180Count} {t.itemsCount}</strong> {t.criticalAlert}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.slowItems}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.totalItems}</Typography>
              </Box>
              <TrendingDown sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #DC143C 0%, #FF6347 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.slow90}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.slow90Count + stats.slow180Count}</Typography>
              </Box>
              <Warning sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.frozenValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.frozenValue)}</Typography>
              </Box>
              <MonetizationOn sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #4682B4 0%, #5F9EA0 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.avgDaysInactive}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{Math.round(stats.avgDaysInactive)}</Typography>
              </Box>
              <Speed sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: '#8B4513' }}>Filters</Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.period} value={periodFilter}
            onChange={(e) => setPeriodFilter(Number(e.target.value))}>
            <MenuItem value={30}>{t.days30}</MenuItem>
            <MenuItem value={60}>{t.days60}</MenuItem>
            <MenuItem value={90}>{t.days90}</MenuItem>
            <MenuItem value={180}>{t.days180}</MenuItem>
          </TextField>
        </Box>

        <Button variant="contained" onClick={loadData} fullWidth
          sx={{ background: 'linear-gradient(45deg, #8B4513 30%, #D2691E 90%)', color: 'white' }}>
          {t.refresh}
        </Button>
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label={t.all} />
          <Tab label={t.slow30} />
          <Tab label={t.slow60} />
          <Tab label={t.slow90} />
          <Tab label={t.slow180} />
          <Tab label={t.byWarehouse} />
          <Tab label={t.byBranch} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '2px solid #8B4513' }}>
          {companyInfo?.logoBase64 && (
            <Box><img src={companyInfo.logoBase64} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} /></Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#8B4513', mb: 0.3 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '9px', color: '#546E7A' }}>{companyInfo?.address || ''} | 📞 {companyInfo?.phone || ''}</Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1, py: 0.5, background: 'linear-gradient(90deg, #8B4513 0%, #D2691E 100%)', color: 'white' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{isRTL ? t.titleAr : t.title}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: '#8B4513' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {(currentTab >= 0 && currentTab <= 4) && (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.lastUpdated}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.daysInactive}</TableCell>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.recommendation}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems(
                  currentTab === 1 ? 'slow30' :
                  currentTab === 2 ? 'slow60' :
                  currentTab === 3 ? 'slow90' :
                  currentTab === 4 ? 'slow180' : undefined
                ).map((row, index) => (
                  <TableRow key={index} sx={{
                    '&:hover': { opacity: 0.85 },
                    bgcolor: row.status === 'slow180' ? (isDark ? '#5c1a1a' : '#FFCDD2') :
                            row.status === 'slow90' ? (isDark ? '#4a1c1c' : '#FFEBEE') :
                            row.status === 'slow60' ? (isDark ? '#4a3520' : '#FFF3E0') :
                            row.status === 'slow30' ? (isDark ? '#1a3a4a' : '#E3F2FD') : 'inherit',
                    '& .MuiTableCell-root': { color: isDark ? '#e0e0e0' : '#263238' },
                  }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500" sx={{ color: isDark ? '#ffffff' : '#1a1a1a' }}>{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#9e9e9e' : '#757575' }}>{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{row.warehouseName}</TableCell>
                    <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: (isDark ? '#ffcc80' : '#e65100') + ' !important' }}>{formatCurrency(row.totalValue)}</TableCell>
                    <TableCell>{formatDate(row.lastUpdated)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{
                        color: row.daysInactive >= 90 ? (isDark ? '#ff6b6b' : '#c62828') :
                               row.daysInactive >= 60 ? (isDark ? '#ffa726' : '#e65100') : (isDark ? '#4fc3f7' : '#0277bd'),
                        fontWeight: 'bold'
                      }}>
                        {row.daysInactive}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getStatusLabel(row.status)} size="small" color={getStatusColor(row.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{
                        color: row.daysInactive >= 90 ? (isDark ? '#ff6b6b' : '#c62828') :
                               row.daysInactive >= 60 ? (isDark ? '#ffa726' : '#e65100') : (isDark ? '#4fc3f7' : '#0277bd'),
                        fontWeight: 'bold'
                      }}>
                        {getRecommendation(row.daysInactive)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredItems(
                  currentTab === 1 ? 'slow30' :
                  currentTab === 2 ? 'slow60' :
                  currentTab === 3 ? 'slow90' :
                  currentTab === 4 ? 'slow180' : undefined
                ).length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {currentTab === 5 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.slowItems}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.frozenValue}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.avgDaysInactive}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouseSummary.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { opacity: 0.85 }, '& .MuiTableCell-root': { color: isDark ? '#e0e0e0' : '#263238' } }}>
                    <TableCell sx={{ fontWeight: '500', color: (isDark ? '#ffffff' : '#1a1a1a') + ' !important' }}>{isRTL ? row.locationNameAr : row.locationName}</TableCell>
                    <TableCell align="right">{row.totalItems}</TableCell>
                    <TableCell align="right" sx={{ color: (isDark ? '#ff6b6b' : '#c62828') + ' !important', fontWeight: 'bold' }}>{row.slowItems}</TableCell>
                    <TableCell align="right" sx={{ color: (isDark ? '#ff6b6b' : '#c62828') + ' !important', fontWeight: 'bold' }}>{formatCurrency(row.frozenValue)}</TableCell>
                    <TableCell align="right">{Math.round(row.avgDaysInactive)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 6 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.branchName}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.slowItems}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.frozenValue}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 'bold' }}>{t.avgDaysInactive}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branchSummary.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { opacity: 0.85 }, '& .MuiTableCell-root': { color: isDark ? '#e0e0e0' : '#263238' } }}>
                    <TableCell sx={{ fontWeight: '500', color: (isDark ? '#ffffff' : '#1a1a1a') + ' !important' }}>{isRTL ? row.locationNameAr : row.locationName}</TableCell>
                    <TableCell align="right">{row.totalItems}</TableCell>
                    <TableCell align="right" sx={{ color: (isDark ? '#ff6b6b' : '#c62828') + ' !important', fontWeight: 'bold' }}>{row.slowItems}</TableCell>
                    <TableCell align="right" sx={{ color: (isDark ? '#ff6b6b' : '#c62828') + ' !important', fontWeight: 'bold' }}>{formatCurrency(row.frozenValue)}</TableCell>
                    <TableCell align="right">{Math.round(row.avgDaysInactive)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.5cm 1cm 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          table { page-break-after: avoid; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
          main, .MuiBox-root { margin: 0 !important; padding: 8px !important; max-width: 100% !important; }
          table thead tr { background: linear-gradient(135deg, #8B4513 0%, #D2691E 100%) !important; -webkit-print-color-adjust: exact; }
          table thead th { background: linear-gradient(135deg, #8B4513 0%, #D2691E 100%) !important; color: white !important; font-weight: bold !important; padding: 4px 6px !important; border: 1px solid #8B4513 !important; font-size: 9px !important; -webkit-print-color-adjust: exact; }
          table tbody td { padding: 3px 6px !important; border: 1px solid #E3F2FD !important; color: #263238 !important; font-size: 8px !important; line-height: 1.2 !important; -webkit-print-color-adjust: exact; }
          table tbody td .MuiTypography-caption { color: #546E7A !important; opacity: 1 !important; }
          .MuiChip-root { height: 16px !important; font-size: 7px !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorError { background-color: #DC143C !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorWarning { background-color: #FF9800 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorInfo { background-color: #2196F3 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #8B4513 !important; -webkit-print-color-adjust: exact; }
          .print-only .MuiPaper-root { -webkit-print-color-adjust: exact; box-shadow: none !important; }
          .print-only img { -webkit-print-color-adjust: exact; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default SlowMovingReport;